import React, { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import PqrSectionModal from './PqrSectionModal'
import SingleSelect from '../../ui/SingleSelect'
import Button from '../../ui/Button'
import SignatureDialog from '../../ui/SignatureDialog'
import TeamMemberFormModal from '../teamMembers/TeamMemberFormModal'
import { useCatalog } from '../../../contexts/CatalogContext'
import type { ProjectDetails } from '../../../types'
import type { TeamMember } from '../../../types/teamMember'
import type { SignatoryDraft } from '../../../types/signatory'
import { resolveMemberUuidForStoredPerson } from '../../../utils/teamMemberLookup'

export type { SignatoryDraft as SignatoryPersonValue }

export const MAX_RECORDED_BY = 4

const DEFAULT_JOB_TITLES = [
  'Welding Engineer',
  'Inspector',
  'Quality Manager',
  'Welder',
  'Supervisor'
] as const

export interface SignaturesValue {
  preparedBy: SignatoryDraft
  reviewedBy: SignatoryDraft
  /** @deprecated Prefer `recordedByList`. Kept for one-release callers. */
  recordedBy?: SignatoryDraft
  /** Floor Sheet Recorded By cards (1..4). */
  recordedByList?: SignatoryDraft[]
}

interface Props {
  open: boolean
  initial: SignaturesValue
  members: TeamMember[]
  teamMembersHref: string
  /** When set, show the Floor Sheet Recorded By block for this weld. */
  recordedByWeldId?: string | null
  projectId?: string
  project?: ProjectDetails
  /** Refetch active team members after creating one from the dropdown. */
  onMembersChanged?: () => Promise<TeamMember[]>
  onApply: (next: SignaturesValue) => void
  onCancel: () => void
}

const EMPTY: SignatoryDraft = { id: '', name: '' }

type ReviewedSlot = 'reviewedBy'
type SignatureTarget = ReviewedSlot | { kind: 'recordedBy'; index: number }
type AddMemberTarget = SignatureTarget

function todayIsoDate(): string {
  return new Date().toISOString().slice(0, 10)
}

function withAutoDate(slot: SignatoryDraft): SignatoryDraft {
  if (slot.date?.trim()) return slot
  if (!slot.name.trim()) return slot
  return { ...slot, date: todayIsoDate() }
}

function normalizeRecordedByList(initial: SignaturesValue): SignatoryDraft[] {
  if (Array.isArray(initial.recordedByList) && initial.recordedByList.length > 0) {
    return initial.recordedByList.slice(0, MAX_RECORDED_BY).map(withAutoDate)
  }
  if (initial.recordedBy) return [withAutoDate(initial.recordedBy)]
  return [{ ...EMPTY }]
}

function memberSelectLabel(m: TeamMember): string {
  const ext = m.memberExternalId?.trim()
  return ext ? `${m.name} (${ext})` : m.name
}

function SignatoryBlock({
  testId,
  label,
  draft,
  members,
  noMembers,
  canAddMember,
  memberById,
  memberOptions,
  jobTitleOptions,
  onSelectMember,
  onUpdate,
  onOpenSignature,
  onClearSignature,
  onAddTeamMember,
  onCreateJobTitle,
  onRemove,
  canRemove
}: {
  testId: string
  label: string
  draft: SignatoryDraft
  members: TeamMember[]
  noMembers: boolean
  canAddMember: boolean
  memberById: Map<string, TeamMember>
  memberOptions: string[]
  jobTitleOptions: string[]
  onSelectMember: (uuid: string) => void
  onUpdate: (patch: Partial<SignatoryDraft>) => void
  onOpenSignature: () => void
  onClearSignature: () => void
  onAddTeamMember?: () => void
  onCreateJobTitle: (value: string) => Promise<string>
  onRemove?: () => void
  canRemove?: boolean
}) {
  const memberUuid = resolveMemberUuidForStoredPerson(
    { id: draft.id, name: draft.name, memberId: draft.memberId },
    members
  )

  const renderMemberLabel = (uuid: string): string => {
    const m = memberById.get(uuid)
    return m ? memberSelectLabel(m) : uuid
  }

  const personLocked = noMembers && !canAddMember

  return (
    <div data-testid={testId} className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <label className="mb-0 block text-sm font-medium text-gray-700">{label}</label>
        {canRemove && onRemove ? (
          <Button
            type="button"
            variant="secondary"
            className="text-xs"
            onClick={onRemove}
            data-testid={`${testId}-remove`}
          >
            Remove
          </Button>
        ) : null}
      </div>
      <SingleSelect
        value={memberUuid}
        options={memberOptions}
        placeholder={
          noMembers && !canAddMember
            ? 'No active team members'
            : `Select ${label.toLowerCase()}…`
        }
        searchPlaceholder="Search team members…"
        interactionLocked={personLocked}
        lockedHint={personLocked ? 'No active team members for this project.' : undefined}
        getOptionLabel={renderMemberLabel}
        getTriggerLabel={renderMemberLabel}
        onChange={onSelectMember}
        addLabel="Add Team Member"
        onAddClick={onAddTeamMember}
      />
      <label className="block text-xs">
        <span className="mb-1 block font-medium text-gray-700">Title</span>
        <SingleSelect
          value={draft.title ?? ''}
          options={jobTitleOptions}
          onChange={(title) => onUpdate({ title })}
          placeholder="Select title…"
          addLabel="Add Job Title"
          createModalTitle="Add Job Title"
          createFieldLabel="Job title"
          createInputPlaceholder="e.g. Welding Engineer"
          searchPlaceholder="Search job title…"
          onCreateOption={onCreateJobTitle}
        />
      </label>
      {draft.date?.trim() ? (
        <p className="text-xs text-gray-600">Date: {draft.date}</p>
      ) : null}
      <div className="rounded-md border border-gray-200 bg-gray-50 p-2">
        {draft.signature ? (
          <img src={draft.signature} alt={`${label} signature`} className="mx-auto max-h-16 max-w-full" />
        ) : (
          <p className="text-center text-xs text-gray-400">No signature yet</p>
        )}
      </div>
      <div className="flex gap-2">
        <Button
          type="button"
          variant="primary"
          className="flex-1 text-sm"
          disabled={!draft.name.trim()}
          onClick={onOpenSignature}
        >
          Add Signature
        </Button>
        {draft.signature ? (
          <Button type="button" variant="secondary" className="text-sm" onClick={onClearSignature}>
            Clear
          </Button>
        ) : null}
      </div>
    </div>
  )
}

export default function SignaturesModal({
  open,
  initial,
  members,
  teamMembersHref,
  recordedByWeldId,
  projectId,
  project,
  onMembersChanged,
  onApply,
  onCancel
}: Props) {
  const { getLabelsMerged, addLabel } = useCatalog()
  const [draft, setDraft] = useState<SignaturesValue>(() => ({
    preparedBy: initial.preparedBy,
    reviewedBy: initial.reviewedBy,
    recordedByList: normalizeRecordedByList(initial),
    recordedBy: normalizeRecordedByList(initial)[0]
  }))
  const [signatureTarget, setSignatureTarget] = useState<SignatureTarget | null>(null)
  const [addMemberTarget, setAddMemberTarget] = useState<AddMemberTarget | null>(null)
  const showRecordedBy = Boolean(recordedByWeldId)
  const canAddMember = Boolean(projectId && project && onMembersChanged)

  useEffect(() => {
    if (!open) return
    const list = normalizeRecordedByList(initial)
    setDraft({
      preparedBy: withAutoDate(initial.preparedBy),
      reviewedBy: withAutoDate(initial.reviewedBy),
      recordedByList: list,
      recordedBy: list[0]
    })
    setAddMemberTarget(null)
  }, [open, initial.preparedBy, initial.reviewedBy, initial.recordedBy, initial.recordedByList]) // eslint-disable-line react-hooks/exhaustive-deps

  const memberById = useMemo(() => {
    const map = new Map<string, TeamMember>()
    members.forEach((m) => map.set(m.id, m))
    return map
  }, [members])

  const memberOptions = useMemo(() => members.map((m) => m.id), [members])
  const noMembers = members.length === 0
  const recordedList = draft.recordedByList ?? [{ ...EMPTY }]

  const jobTitleOptions = useMemo(
    () =>
      getLabelsMerged('team_member_job_title', [
        ...DEFAULT_JOB_TITLES,
        draft.reviewedBy.title ?? '',
        ...recordedList.map((p) => p.title ?? '')
      ]),
    [getLabelsMerged, draft.reviewedBy.title, recordedList]
  )

  const applyMemberToReviewed = (member: TeamMember) => {
    setDraft((prev) => {
      const current = prev.reviewedBy
      return {
        ...prev,
        reviewedBy: {
          ...current,
          id: member.memberExternalId,
          memberId: member.id,
          name: member.name,
          title: member.jobTitle?.trim() || current.title || '',
          date: current.date?.trim() || todayIsoDate()
        }
      }
    })
  }

  const applyMemberToRecorded = (index: number, member: TeamMember) => {
    setDraft((prev) => {
      const list = [...(prev.recordedByList ?? [{ ...EMPTY }])]
      const current = list[index] ?? EMPTY
      list[index] = {
        ...current,
        id: member.memberExternalId,
        memberId: member.id,
        name: member.name,
        title: member.jobTitle?.trim() || current.title || '',
        date: (current.date ?? '').trim() || todayIsoDate()
      }
      return { ...prev, recordedByList: list, recordedBy: list[0] }
    })
  }

  const handleSelectReviewedBy = (uuid: string) => {
    const member = memberById.get(uuid)
    if (!member) return
    applyMemberToReviewed(member)
  }

  const handleSelectRecorded = (index: number) => (uuid: string) => {
    const member = memberById.get(uuid)
    if (!member) return
    applyMemberToRecorded(index, member)
  }

  const updateReviewedBy = (patch: Partial<SignatoryDraft>) => {
    setDraft((prev) => ({ ...prev, reviewedBy: { ...prev.reviewedBy, ...patch } }))
  }

  const updateRecordedAt = (index: number, patch: Partial<SignatoryDraft>) => {
    setDraft((prev) => {
      const list = [...(prev.recordedByList ?? [{ ...EMPTY }])]
      list[index] = { ...(list[index] ?? EMPTY), ...patch }
      return { ...prev, recordedByList: list, recordedBy: list[0] }
    })
  }

  const handleSaveSignature = (
    signature: string,
    name: string,
    _comment?: string,
    stampImage?: string
  ) => {
    if (!signatureTarget) return
    if (typeof signatureTarget === 'object' && signatureTarget.kind === 'recordedBy') {
      const current = recordedList[signatureTarget.index] ?? EMPTY
      updateRecordedAt(signatureTarget.index, {
        name: name || current.name,
        signature,
        stampImage,
        signedAt: new Date().toISOString(),
        date: current.date?.trim() ? current.date : todayIsoDate()
      })
    } else {
      setDraft((prev) => {
        const current = prev.reviewedBy
        return {
          ...prev,
          reviewedBy: {
            ...current,
            name: name || current.name,
            signature,
            stampImage,
            signedAt: new Date().toISOString(),
            date: current.date?.trim() ? current.date : todayIsoDate()
          }
        }
      })
    }
    setSignatureTarget(null)
  }

  const handleClearReviewedBy = () => {
    updateReviewedBy({
      signature: undefined,
      stampImage: undefined,
      signedAt: undefined
    })
  }

  const handleClearRecorded = (index: number) => {
    updateRecordedAt(index, {
      signature: undefined,
      stampImage: undefined,
      signedAt: undefined
    })
  }

  const addRecordedBy = () => {
    setDraft((prev) => {
      const list = [...(prev.recordedByList ?? [{ ...EMPTY }])]
      if (list.length >= MAX_RECORDED_BY) return prev
      list.push({ ...EMPTY })
      return { ...prev, recordedByList: list, recordedBy: list[0] }
    })
  }

  const removeRecordedBy = (index: number) => {
    setDraft((prev) => {
      const list = [...(prev.recordedByList ?? [{ ...EMPTY }])]
      if (list.length <= 1) return prev
      list.splice(index, 1)
      return { ...prev, recordedByList: list, recordedBy: list[0] }
    })
  }

  const handleTeamMemberSaved = async (saved: TeamMember) => {
    const target = addMemberTarget
    setAddMemberTarget(null)
    let member = saved
    if (onMembersChanged) {
      const refreshed = await onMembersChanged()
      member = refreshed.find((m) => m.id === saved.id) ?? saved
    }
    if (!target) return
    if (typeof target === 'object' && target.kind === 'recordedBy') {
      applyMemberToRecorded(target.index, member)
    } else {
      applyMemberToReviewed(member)
    }
  }

  const handleCreateJobTitle = async (value: string) => {
    await addLabel('team_member_job_title', value)
    return value.trim()
  }

  const initialList = normalizeRecordedByList(initial)
  const isDirty =
    JSON.stringify(draft.reviewedBy) !== JSON.stringify(initial.reviewedBy) ||
    (showRecordedBy && JSON.stringify(recordedList) !== JSON.stringify(initialList))

  const activeSlot: SignatoryDraft | null = (() => {
    if (!signatureTarget) return null
    if (typeof signatureTarget === 'object' && signatureTarget.kind === 'recordedBy') {
      return recordedList[signatureTarget.index] ?? EMPTY
    }
    return draft.reviewedBy
  })()

  const signatureDialogTitle =
    signatureTarget === 'reviewedBy' ? 'Reviewed By Signature' : 'Recorded By Signature'

  const blockShared = {
    members,
    noMembers,
    canAddMember,
    memberById,
    memberOptions,
    jobTitleOptions,
    onCreateJobTitle: handleCreateJobTitle
  }

  return (
    <>
      <PqrSectionModal
        open={open}
        title="Edit signatories"
        isModalDirty={isDirty}
        onApply={() => {
          onApply({
            preparedBy: { ...EMPTY },
            reviewedBy: draft.reviewedBy,
            ...(showRecordedBy
              ? { recordedByList: recordedList, recordedBy: recordedList[0] }
              : {})
          })
        }}
        onCancel={onCancel}
        panelMaxWidthClassName="max-w-3xl"
        panelMaxHeightClassName="max-h-[80vh]"
      >
        {showRecordedBy ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-medium text-gray-700">
                Recorded By
                {recordedByWeldId ? ` (${recordedByWeldId})` : ''}
              </p>
              <Button
                type="button"
                variant="secondary"
                className="text-sm"
                disabled={recordedList.length >= MAX_RECORDED_BY}
                onClick={addRecordedBy}
                data-testid="signatures-recorded-by-add"
              >
                Add another
              </Button>
            </div>
            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              {recordedList.map((person, index) => (
                <SignatoryBlock
                  key={`recorded-by-${index}`}
                  testId={`signatures-recorded-by-block-${index}`}
                  label={
                    recordedList.length > 1 ? `Recorded By ${index + 1}` : 'Recorded By'
                  }
                  draft={person}
                  {...blockShared}
                  onSelectMember={handleSelectRecorded(index)}
                  onUpdate={(patch) => updateRecordedAt(index, patch)}
                  onOpenSignature={() => setSignatureTarget({ kind: 'recordedBy', index })}
                  onClearSignature={() => handleClearRecorded(index)}
                  onAddTeamMember={
                    canAddMember
                      ? () => setAddMemberTarget({ kind: 'recordedBy', index })
                      : undefined
                  }
                  canRemove={recordedList.length > 1}
                  onRemove={() => removeRecordedBy(index)}
                />
              ))}
            </div>
            {recordedList.length >= MAX_RECORDED_BY ? (
              <p className="text-xs text-gray-500">Maximum {MAX_RECORDED_BY} Recorded By people.</p>
            ) : null}
          </div>
        ) : null}

        <div className={`${showRecordedBy ? 'mt-5 border-t border-gray-200 pt-5' : ''} grid grid-cols-1 gap-5 sm:grid-cols-2`}>
          <SignatoryBlock
            testId="signatures-reviewed-by-block"
            label="Reviewed By"
            draft={draft.reviewedBy}
            {...blockShared}
            onSelectMember={handleSelectReviewedBy}
            onUpdate={(patch) => updateReviewedBy(patch)}
            onOpenSignature={() => setSignatureTarget('reviewedBy')}
            onClearSignature={handleClearReviewedBy}
            onAddTeamMember={
              canAddMember ? () => setAddMemberTarget('reviewedBy') : undefined
            }
          />
        </div>

        <div className="mt-8">
          <p className="text-xs text-gray-500">
            {canAddMember ? (
              <>
                Don&apos;t see who you need? Use <strong>Add Team Member</strong> in the person
                dropdown, or{' '}
                <Link to={teamMembersHref} className="font-medium text-brand-red-600 underline">
                  Manage team members
                </Link>
                .
              </>
            ) : (
              <>
                Don&apos;t see who you need?{' '}
                <Link to={teamMembersHref} className="font-medium text-brand-red-600 underline">
                  Manage team members
                </Link>
                .
              </>
            )}
          </p>
          <p className="mt-2 text-xs text-gray-500">
            Changes are staged in the preview. Click <strong>Save</strong> or{' '}
            <strong>Save &amp; Generate Report</strong> on the page header to persist them.
          </p>
        </div>
      </PqrSectionModal>

      <SignatureDialog
        isOpen={signatureTarget !== null}
        onClose={() => setSignatureTarget(null)}
        onSave={handleSaveSignature}
        title={signatureDialogTitle}
        initialName={activeSlot?.name ?? ''}
        initialSignature={activeSlot?.signature ?? null}
        initialStampImage={activeSlot?.stampImage ?? null}
      />

      {projectId && project ? (
        <TeamMemberFormModal
          projectId={projectId}
          project={project}
          open={addMemberTarget !== null}
          onClose={() => setAddMemberTarget(null)}
          member={null}
          existingMemberIds={members.map((m) => m.memberExternalId)}
          onSaved={(saved) => {
            void handleTeamMemberSaved(saved)
          }}
        />
      ) : null}
    </>
  )
}

export { EMPTY as EMPTY_SIGNATORY }
